/**
 * PostHog feature flag keys — the single source of truth across frontend,
 * backend, and agent-service.
 *
 * Flags whose rollout reached 100% get retired — gate removed, entry
 * deleted here and in the Python mirror, then the PostHog flag disabled
 * only after the code deploys (last code gate pruned 2026-07-28, the protocol
 * video chapters flag). The
 * exported `FEATURE_FLAGS` object stays exported even when
 * empty so dependent files keep type-checking.
 *
 * **How to add a flag:**
 * 1. Pick the key here (lowercase-kebab-case, matches the key in the
 *    PostHog dashboard).
 * 2. Decide the scope deliberately. The full per-layer contract is in the
 *    `feature-flags` skill; `rules/analytics.md` §Feature flags is the rule.
 *      - **Per-user** rollout (most common): distinct_id is the internal
 *        Prisma UUID. First release condition: the "Bower Internal" cohort
 *        (222939) at 100%, or the `is_internal = true` person property if
 *        the agent service evaluates the flag (it cannot satisfy the
 *        email-based cohort; see below). Then beta partners, then general.
 *      - **Per-workspace** rollout (shared workspace features): distinct_id
 *        is the workspaceId, every caller also sends `{ workspace_id }` as
 *        a person property, and the browser reads the answer from a backend
 *        route. First release condition: the Internal Tester Workspaces
 *        cohort (597694, a dynamic cohort on `workspace_id`) at 100%.
 * 3. Add the flag in PostHog with that scope and first release condition,
 *    and state the scope in capitals in the comment above the key.
 * 4. In code: import the key from here. Don't hardcode strings.
 *
 * **Staging only:** to roll out on staging only, add a release condition
 * `environment exact staging`; never add a per-environment code gate. Every
 * surface sends `environment` on every evaluation (the `feature-flags` skill,
 * "Staging only", has the detail).
 *
 * **Dev access:** the sanctioned mechanism is the first release condition.
 * An `@bowerlabs.ai` signup opens every per-user gate, and a per-workspace
 * gate opens for your workspace once it is on the allowlist, on the SAME
 * code path prod runs. Do not add environment-conditional bypasses.
 *
 * There is NO environment short-circuit anywhere. This header used to claim
 * `posthogService.isFeatureEnabled` returns `true` off production; it does
 * not, and never did. `rules/analytics.md` corrected the same claim on its own
 * side and says why it mattered — it reads as "gates are open by default in
 * dev" for gates that are not. Dev, test and CI all evaluate for real and fall
 * back to the caller's default.
 *
 * **Step 3 works server-side because the backend hydrates the ADDRESS.** The
 * "Bower Internal" cohort (222939) is two legs — `email_domain exact
 * [bowerlabs.ai, bower-e2e.test]` OR `email exact <17-address allowlist>` —
 * and #2309 made the backend forward both, so a flag written exactly the way
 * step 3 says resolves the same server-side as it does in the browser.
 * `docs/architecture/feature-flag-evaluation.md` § The same distinct_id is not enough has the mechanism
 * and why it was broken before that.
 *
 * The backend also forwards `is_internal` as a real BOOLEAN, **but only when
 * true**, and it is defensive rather than load-bearing: the cohort has no
 * `is_internal` leg, so this only matters for a condition written directly
 * against the person property. Never send it as `false` — a supplied property
 * overrides the stored one, and several people carry a stored
 * `is_internal: true` that no domain check would reproduce.
 *
 * **The agent service does NOT hydrate.** A flag read on that surface
 * (`routes/live.py`, `agents/research_execution_policy.py`) still resolves
 * against stored properties, so it reads false on staging and for
 * consent-declined users, and an agent-side gate runs BEFORE any backend MCP
 * gate. Account for it when a flag gates agent behaviour.
 *
 * **Python mirror:** keep
 * `packages/agent-service/src/agent_service/constants/feature_flags.py`
 * in lockstep with this file.
 */
export const FEATURE_FLAGS = {
    // Social sign-in (Google + Microsoft). Gates the login/signup buttons AND
    // doubles as the kill-switch for the dashboard connector hero (the hero
    // additionally self-gates on the user's SSO provider). ONE flag for the
    // whole feature.
    //
    // RAMP AS A 0/100 KILL-SWITCH, not a graduated %. The two surfaces evaluate
    // on different distinct_id spaces — the buttons PRE-AUTH on the anonymous id,
    // the hero POST-AUTH on the internal Prisma UUID — so a partial % rollout
    // buckets them independently and a user could get the buttons but not the
    // hero. At 0/100 they agree. (If a graduated ramp is ever needed, split into
    // two flags: a pre-auth % flag and a post-auth is_internal-cohort flag.)
    //
    // PRE-AUTH detail: the anonymous-id evaluation means the is_internal cohort
    // can't target the buttons — internal testing happens on dev/emulator.
    // identify() only runs post-auth as the login page unmounts, so there's no
    // mid-flow flip risk (the 2026-06 flag-flip-on-identify incident class).
    //
    // PERMANENT (David, 2026-10-05): kept ON as a kill switch, never retired;
    // listed in ACKNOWLEDGED_KEPT_ON in scripts/check-feature-flag-coverage.mjs.
    SSO_LOGIN_ENABLED: 'sso-login-enabled',
    // Analytics on by default where opt-in consent is not required. When this
    // evaluates TRUE for a browser that has made no cookie decision, the browser
    // gets full analytics (events, autocapture, replay, GA4) without the banner,
    // stored as a `region_default` consent basis that Settings can switch off.
    // When it evaluates FALSE for a browser holding that basis, the default is
    // withdrawn and the banner returns. An explicit Accept or Essential-only
    // choice always wins over it, in both directions (`utils/cookieConsent.ts`).
    //
    // OUTSIDE the per-user / per-workspace contract, like SSO_LOGIN_ENABLED: it
    // is evaluated by posthog-js before consent and usually before login, on the
    // anonymous id, and its condition is GEOGRAPHY, not identity. The only
    // condition that may open it is `$geoip_country_code` IS SET and NOT IN the
    // opt-in jurisdictions (EU/EEA, UK and its dependencies, Switzerland, and the
    // territories carrying their own codes). PostHog computes that property from
    // the browser's IP on every /flags request, so an unknown location matches
    // nothing and keeps the banner (fails closed). The country list lives in the
    // PostHog condition, so changing it is a flag edit: re-read it against
    // `docs/compliance/cookie-consent.md` before saving.
    //
    // Read by the frontend only. Off (or a /flags error) is today's behavior for
    // every browser that was never defaulted on.
    ANALYTICS_REGION_DEFAULT: 'analytics-region-default',
    // Live model picker and Pre-GA permission. Every live session opens on GA
    // Gemini 3.8 Live by default and may name 2.5 without this flag; it gates
    // only (a) whether the Settings model picker shows for a WORKSPACE and (b)
    // whether that workspace may ask for the Pre-GA Extended Thinking model
    // (`default-thinking`, refused to PHI workspaces regardless). It used to gate
    // 3.8 itself, before 3.8 Live became the default on 2026-10-06.
    //
    // PER-WORKSPACE, so `distinct_id` is the workspace id and every release
    // condition must match on `workspace_id` (inline, or the internal workspaces
    // cohort built on it). It must NOT be the `is_internal` cohort: a workspace
    // UUID has no Person record, so a person-property condition never matches
    // and the flag reads false while looking configured. Only admit workspaces
    // whose MEMBERSHIP we govern: every member of one can reach Extended
    // Thinking, whose Pre-GA terms forbid external end users. If a condition
    // uses the internal workspaces cohort, adding a workspace to that cohort
    // opens it too.
    //
    // The scope is also why it resolves at all. A per-user cohort flag matches on
    // person properties PostHog has STORED, which needs an `$identify` event and
    // therefore analytics consent, so a large minority of people could never
    // match one (`rules/analytics.md`). This depends on nothing stored.
    //
    // The agent resolves it ONCE per session at the WebSocket handshake, reads
    // it only when the session names a Pre-GA variant, and fails closed.
    LIVE_MODEL_NEXT: 'live-model-next',
    // Video insights: Gemini agentic video over a lab recording, producing
    // PROPOSALS (chapters / draft protocol / enrichment / deviations) a human
    // confirms. Per-user rollout; first release condition: is_internal = true
    // cohort at 100%. Checked at the route (404 when off) AND in the job.
    VIDEO_INSIGHTS_ENABLED: 'video-insights-enabled',
    // Live calls on the web (demo prototype), the ONE flag for all of it: the
    // call-started toast, its Open call button, the `/live/call` page and its
    // rail + sidebar entries. When the mobile app joins a LiveKit call it tells
    // the backend, which pushes `call:started` to the rest of the workspace.
    // PER-USER (distinct_id = internal Prisma UUID): the backend checks the
    // CALLER (404 when off), the web checks the VIEWER. Release condition:
    // Internal Users cohort (222939) at 100% AND `environment` in
    // [development, staging]. Retire with the mobile call MVP it mirrors.
    LIVE_CALL_PREVIEW_ENABLED: 'live-call-preview-enabled',
    // Live echo containment: when Bower's own voice comes back through the mic
    // and is transcribed as the user's turn, the agent drops that utterance
    // before any classifier, stop-phrase matcher, memory write or empty-turn
    // recovery can act on it (`agent_service/services/live_echo.py`). A KILL
    // SWITCH for a text heuristic that can also drop a real answer, so OFF is
    // the safe side: off, missing or unreachable all mean today's behavior, and
    // the verdict is still logged and put on the live-turn span either way.
    //
    // PER-USER (distinct_id = internal Prisma UUID). AGENT-SIDE ONLY, and the
    // agent does not hydrate person properties the way the backend does
    // (header above): it resolves against STORED properties, except that it
    // forwards the backend's `is_internal` when that is true. So write the first
    // release condition against the `is_internal = true` PERSON PROPERTY, not
    // the email-based "Bower Internal" cohort, which the agent cannot satisfy
    // for a person PostHog has not stored. Resolved once per live session, off
    // the event loop, and never blocks the session opening.
    //
    // PERMANENT (David, 2026-10-05): kept ON as an operational kill switch, never
    // retired; listed in ACKNOWLEDGED_KEPT_ON in
    // scripts/check-feature-flag-coverage.mjs.
    LIVE_ECHO_CONTAINMENT: 'live-echo-containment',
    // Live agent memory: whether LIVE mode uses agent memory (Memory Bank). ON,
    // live preloads the remembered facts into its instruction, offers
    // `load_memory`, and writes the user's utterances to Memory Bank, exactly as
    // before the flag existed. OFF for a user, live does none of the three; the
    // rest of the personalization block (declared name, role, projects) stays.
    // Chat is unaffected either way. Added 2026-09-30 after a remembered "100 mL"
    // overrode a protocol step's "about 50 mL" mid-run.
    //
    // A KILL SWITCH THAT FAILS OPEN, unlike the usual fail-closed rule: its ON
    // position is the behavior production already runs, so a missing flag, no
    // PostHog client, a timeout or an error all read TRUE and change nothing.
    // Created ACTIVE at 100% with no conditions. PER-USER (distinct_id =
    // internal Prisma UUID). AGENT-SIDE ONLY, resolved once per live session
    // alongside the connect-time memory fetch. To switch one user off, add a
    // `distinct_id is_not <uuid>` person property to the 100% group (the Python
    // SDK sends `distinct_id` as a person property on every evaluation). Parked
    // ON permanently, so it is in `ACKNOWLEDGED_KEPT_ON`
    // (scripts/check-feature-flag-coverage.mjs) to keep repo-hygiene from
    // retiring it for holding 100%.
    LIVE_AGENT_MEMORY: 'live-agent-memory',
    // Live says a failed note, table or other workspace write aloud when the
    // model claimed it worked or said nothing (#4458). PER-USER, read by the
    // agent service only (services/failed_write_said.py); mirrored here for
    // lockstep. First release condition: `is_internal = true`.
    LIVE_WRITE_FAILURE_GUARD: 'live-write-failure-guard',
    // Live says nothing when the Run start of a reopened Live session resumes a
    // run at the step the user's last session already said (L2c: "picking up
    // where you left off" repeated on every reopen). PER-USER, read by the agent
    // service only (routes/live_execution_start.py); mirrored here for lockstep.
    // First release condition: `is_internal = true`.
    LIVE_QUIET_REOPEN: 'live-quiet-reopen',
    // A Live photo or video that did not save gets one definite line that offers
    // the phone's camera, and what the user said about it is kept as a note (L2d,
    // #4101). PER-USER, read by the agent service only
    // (services/capture_failure_said.py); mirrored here for lockstep. First
    // release condition: `is_internal = true`.
    LIVE_CAPTURE_FAILURE_LINE: 'live-capture-failure-line',
    // Live biases its input transcription toward the workspace's own names
    // (protocol titles, equipment and reagent names, the reopened run's materials) on a
    // model that takes `custom_vocabulary` (L2f). PER-USER, read by the agent
    // service only (services/live_vocabulary.py); mirrored here for lockstep.
    // First release condition: `is_internal = true`.
    LIVE_DOMAIN_VOCABULARY: 'live-domain-vocabulary',
    // A Live session's update_artifact takes a table cell by row and column and
    // reads back what it now holds, and insert_figure_into_note accepts a Live
    // capture's own file (L2e). Off is main: the published tools, schemas and
    // validation messages. PER-USER, read by the backend for a request that proves it
    // is Live (mcp/live-cell-edit.ts, services/artifact/figure-to-note.capture.ts),
    // never for an external client, and by the agent once per Live connection to
    // decide whether to send that proof. First release condition: `is_internal = true`.
    LIVE_CELL_EDIT: 'live-cell-edit',
    // Live runs the chat entity-link guards (fabricated host, unverified id) on
    // the agent's captions and so on the saved run transcript (L2k, #3556).
    // PER-USER, read by the agent service only (routes/live_caption_links.py);
    // mirrored here for lockstep. First release condition: `is_internal = true`.
    LIVE_LINK_GUARDS: 'live-link-guards',
    // Live command center (`docs/superpowers/plans/2026-10-23-live-command-
    // center-plan.md`), the whole project behind ONE switch (David, 2026-10-26):
    // the 1 s voice recording answer (slice 0c), command acks and `seq`, device
    // commands, the confirm card, step targeting, earcons and the shutter's
    // video (slice 2), captures as artifacts (slice 3), recording on the device
    // (slice 6), inputs (slice 7), the session plan (slice 5) and device step
    // audio (slice 8). Each piece also needs its client to declare the matching
    // client feature; off, or an undeclared client, is byte-for-byte today.
    //
    // PER-USER (distinct_id = internal Prisma UUID). Read by the agent at Live
    // connect (it sends no email) and by the backend per request (it sends
    // `is_internal` from the database): the first release condition is the
    // `is_internal = true` PERSON PROPERTY, which both can match. Turning it off
    // takes effect from each Live session's next connect. Default off.
    LIVE_COMMAND_CENTER: 'live-command-center',
    // Image auto-convert routing by image kind. On, the page-analysis call also
    // classifies the image (`imageKind`: notebook page, whiteboard, instrument
    // display, ...) and `decideAutoConvertTextFound` routes a document-like image
    // with readable text to a note, whatever confidence the model gave itself.
    // Off, the prompt and the decision are byte-for-byte today's.
    //
    // PER-USER (distinct_id = internal Prisma UUID), read by the backend's
    // automatic image extraction job only. First release condition: the
    // Internal Users cohort (222939). Default off.
    IMAGE_KIND_NOTE_ROUTING: 'image-kind-note-routing',
    // Unified setup list (usability plan 2026-10-29, Workstream D). On, a new
    // workspace membership is enrolled in the per-membership checklist-2026-11
    // (owner / invitee / viewer variants, signals scoped to the user). Read ONCE,
    // server-side, when a membership is enrolled; the stamped `enrolledVersion`
    // decides from then on, never the live flag. Off, nothing is written and the
    // per-user checklist runs exactly as before.
    //
    // PER-USER (distinct_id = internal Prisma UUID), read by the backend only.
    // First release condition: the Internal Users cohort (222939). Default off.
    ONBOARDING_UNIFIED_SETUP: 'onboarding-unified-setup',
    // Research literature fan-out (agent primitives audit 2026-10-23, its "open
    // option"). On, the research assistant gets one `literature_search` tool in
    // place of `web_search` and `scholarly_search`: it takes one to four
    // questions and runs both searches for every question concurrently in code,
    // returning one result with one citation numbering. Read ONCE per chat turn
    // by the agent service and bound for the turn. Off, research's requests are
    // byte-for-byte today's.
    //
    // PER-WORKSPACE (distinct_id = workspace UUID, `workspace_id` sent), read by
    // the agent service only. First release condition: the Internal Tester
    // Workspaces cohort (597694). Default off.
    RESEARCH_LITERATURE_FANOUT: 'research-literature-fanout',
    // Session-end rating (agent quality plan 2026-11-01, M3). On, the web app
    // asks for one tap at the end of a session: under the last chat reply (at
    // most once per conversation) and in a toast after a Live session ends. Both
    // score the session's own trace as `user-feedback`, the same path as the
    // per-message thumb. On, the Live page also declares the `session_trace`
    // client feature, which is what makes the agent service send the trace id.
    // Off, nothing is shown or declared: byte-for-byte today.
    //
    // PER-USER (distinct_id = internal Prisma UUID), read by the frontend only.
    // First release condition: the Internal Users cohort (222939). Default off.
    SESSION_END_RATING_ENABLED: 'session-end-rating-enabled',
    // Chat answer honesty (agent quality plan 2026-11-01, C6). On, root, writer
    // and research read one more rules block: no LaTeX in tables or copyable
    // text, citations kept through a rewrite, whether a URL was read, sourced
    // literature comparisons, ambiguous acronyms and dictated values kept or
    // asked about, "I can't observe X", no facts from an unrelated note, a
    // transient tool fault reported as "try again", no model version or cost.
    // Read ONCE when a chat session is created and kept in its state. Off, every
    // chat prompt is byte-for-byte today's.
    //
    // PER-USER (distinct_id = internal Prisma UUID), read by the agent service
    // only. First release condition: the `is_internal = true` person property.
    // Default off.
    CHAT_ANSWER_HONESTY: 'chat-answer-honesty',
    // Scope of general help (agent quality plan 2026-11-01, C8). On, root reads a
    // short rules block: a brief general request that needs no facts about the
    // world (a headline, a general how-to) is answered instead of declined.
    // Current events and real-world facts stay under the Scope rule. Read ONCE when
    // a chat session is created and kept in its state. Off, every chat prompt is
    // byte-for-byte today's.
    //
    // PER-USER (distinct_id = internal Prisma UUID), read by the agent service
    // only. First release condition: the `is_internal = true` person property.
    // Default off.
    CHAT_GENERAL_HELP: 'chat-general-help',
};
//# sourceMappingURL=featureFlags.js.map